import { apiMethods } from '../services/api';

// Cache for the hijri calendar data to avoid repeated API calls
let hijriCalendarCache: any = null;
let cacheTimestamp: number | null = null;
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

/**
 * Get the hijri calendar data for a given year from the backend
 */
const getHijriCalendar = async (year: number): Promise<any> => {
  const now = Date.now();
  
  // Return cached value if it's still fresh and for the same year
  if (hijriCalendarCache?.year === year && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
    return hijriCalendarCache;
  }
  
  try {
    const calendar = await apiMethods.getHijriCalendar(year);
    hijriCalendarCache = calendar;
    cacheTimestamp = now;
    return calendar;
  } catch (error) {
    console.warn('Error fetching Hijri calendar from backend:', error);
    throw error;
  }
};

/**
 * Find hijri date for a given Gregorian date using backend API
 */
const findHijriDate = async (year: number, month: number, day: number) => {
  const calendar = await getHijriCalendar(year);
  const gregorianDateStr = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  
  const dateEntry = calendar.dates.find((entry: any) => entry.gregorian_date === gregorianDateStr);
  
  if (!dateEntry) {
    throw new Error(`No hijri date found for ${gregorianDateStr}`);
  }
  
  return {
    hy: dateEntry.hijri_year,
    hm: dateEntry.hijri_month,
    hd: dateEntry.hijri_day
  };
};

/**
 * Find Gregorian date for a given Hijri date using backend API
 */
const findGregorianDate = async (hijriYear: number, hijriMonth: number, hijriDay: number, searchYear: number = new Date().getFullYear()) => {
  const calendar = await getHijriCalendar(searchYear);
  
  const dateEntry = calendar.dates.find((entry: any) => 
    entry.hijri_year === hijriYear && 
    entry.hijri_month === hijriMonth && 
    entry.hijri_day === hijriDay
  );
  
  if (!dateEntry) {
    // Try the next year if not found in current year
    try {
      const nextYearCalendar = await getHijriCalendar(searchYear + 1);
      const nextYearEntry = nextYearCalendar.dates.find((entry: any) => 
        entry.hijri_year === hijriYear && 
        entry.hijri_month === hijriMonth && 
        entry.hijri_day === hijriDay
      );
      
      if (nextYearEntry) {
        const [year, month, day] = nextYearEntry.gregorian_date.split('-').map(Number);
        return { gy: year, gm: month, gd: day };
      }
    } catch (error) {
      // Ignore error, fall through to main error
    }
    
    throw new Error(`No gregorian date found for ${hijriDay}/${hijriMonth}/${hijriYear}`);
  }
  
  const [year, month, day] = dateEntry.gregorian_date.split('-').map(Number);
  return {
    gy: year,
    gm: month,
    gd: day
  };
};

/**
 * Convert Gregorian date to Hijri (using backend API with offset already applied)
 */
export const toHijriWithOffset = async (year: number, month: number, day: number) => {
  return await findHijriDate(year, month, day);
};

/**
 * Convert Hijri date to Gregorian (using backend API with offset already applied)
 */
export const toGregorianWithOffset = async (year: number, month: number, day: number) => {
  return await findGregorianDate(year, month, day);
};

/**
 * Get the current Hijri offset setting from backend
 */
export const getHijriOffset = async (): Promise<number> => {
  try {
    const settings = await apiMethods.getHijriSettings();
    return settings.hijri_offset || 0;
  } catch (error) {
    console.warn('Error fetching Hijri offset:', error);
    return 0;
  }
};

/**
 * Clear the cache (useful when offset is updated)
 */
export const clearHijriOffsetCache = () => {
  hijriCalendarCache = null;
  cacheTimestamp = null;
};
